import { useState, useEffect, useRef, useCallback } from "react";
import {
  MessageSquareText,
  AppWindow,
  TerminalSquare,
  Network,
  ArrowRight,
  RotateCcw,
  X,
  Search,
  Sparkles,
  Plus,
  History,
  Menu,
  ZoomIn,
  ZoomOut,
  Scan,
  Workflow,
  Maximize2,
} from "lucide-react";

const ROTATE_MS = 5000;

const COPILOTS = [
  {
    icon: MessageSquareText,
    name: "Scenario Advisor",
    role: "Your on-page hint tutor",
    tag: "LAB PAGE",
    title: "scenario-advisor · lab page",
  },
  {
    icon: AppWindow,
    name: "In-Box Lab Assistant",
    role: "Guidance without leaving the VM",
    tag: "INSIDE THE BOX",
    title: "lab-assistant · attack-box",
  },
  {
    icon: TerminalSquare,
    name: "Omnirange Copilot",
    role: "Red & blue exploitation copilot",
    tag: "IN-BOX TERMINAL",
    title: "bash — omnirange-copilot",
  },
  {
    icon: Network,
    name: "AI Architect",
    role: "Design networks by conversation",
    tag: "TOPOLOGY BUILDER",
    title: "ai-architect · canvas",
  },
];

function usePrefersReducedMotion() {
  const [reduce, setReduce] = useState(
    () =>
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches,
  );
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const on = () => setReduce(mq.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  return reduce;
}

/* ── Stage 0 · Scenario Advisor (green) ─────────────────────────────────── */
function AdvisorStage() {
  return (
    <div className="absolute inset-0">
      <div
        aria-hidden
        className="absolute inset-0 rounded-xl border border-(--color-border) bg-(--color-bg-card) p-4 opacity-50"
      >
        <div className="mb-3 text-[13px] font-semibold text-(--color-text-muted)">
          Challenge · Login Bypass
        </div>
        {["82%", "100%", "60%"].map((w, i) => (
          <div
            key={i}
            className="mb-2.5 h-2 rounded bg-(--color-text-primary)/5"
            style={{ width: w }}
          />
        ))}
        <div className="mt-4 mb-2.5 h-2 w-[82%] rounded bg-(--color-text-primary)/5" />
        <div className="h-2 w-[60%] rounded bg-(--color-text-primary)/5" />
      </div>
      <div className="absolute inset-y-0 right-0 flex w-full max-w-87.5 flex-col overflow-hidden rounded-2xl border border-(--color-border) bg-(--color-bg-card) shadow-[0_30px_70px_-30px_#000]">
        <div className="flex shrink-0 items-center gap-2 border-b border-(--color-border) px-4 py-3">
          <span className="text-[15px] font-bold text-(--color-text-primary)">
            Scenario Advisor
          </span>
          <span className="ml-auto flex items-center gap-3 text-(--color-text-muted)">
            <RotateCcw className="h-4 w-4" />
            <X className="h-4 w-4" />
          </span>
        </div>
        <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto p-4">
          <div className="flex items-start gap-2.5">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-(--color-primary)/40 bg-(--color-primary-100) qv-mono text-[10px] font-bold text-(--color-primary)">
              QV
            </span>
            <div className="rounded-2xl rounded-tl-sm border border-(--color-border) bg-(--color-bg) px-3.5 py-2 text-[12.5px] leading-relaxed text-(--color-text-primary)">
              Hi! 👋 Need help with this box?
              <br />
              <br />
              How can I assist you?
            </div>
          </div>
          <div className="mt-auto flex flex-col items-start gap-2">
            {[
              "Can you help me understand this exploit?",
              "What kind of box is this?",
              "I'm stuck on a question",
            ].map((s) => (
              <span
                key={s}
                className="rounded-full border border-(--color-primary)/30 bg-(--color-primary-50) px-3.5 py-1.5 text-[12px] font-medium text-(--color-primary)"
              >
                {s}
              </span>
            ))}
          </div>
        </div>
        <div className="flex shrink-0 flex-col gap-2.5 border-t border-(--color-border) px-4 py-3">
          <div className="flex items-center gap-2.5 qv-mono text-[10px] text-(--color-text-muted)">
            <span className="h-1 flex-1 overflow-hidden rounded-full bg-(--color-bg)">
              <span className="block h-full w-[80%] rounded-full bg-(--color-primary)" />
            </span>
            0 / 500k
          </div>
          <div className="rounded-lg border border-(--color-border) px-3 py-2 text-[12px] text-(--color-text-muted)">
            Write here…
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Stage 1 · In-Box Lab Assistant (navy) ──────────────────────────────── */
function LabAssistantStage() {
  const nv = {
    bg: "#0f1826",
    border: "rgba(120,150,200,.16)",
    bubble: "#1b2740",
    chip: "#1a2337",
    sub: "#8ea1c2",
    text: "#cdd8ec",
  };
  return (
    <div className="absolute inset-0">
      <div
        aria-hidden
        className="absolute inset-0 rounded-xl opacity-50"
        style={{
          background:
            "repeating-linear-gradient(45deg,#0d1512,#0d1512 12px,#0a110e 12px,#0a110e 24px)",
        }}
      />
      <div
        className="absolute inset-y-0 right-0 flex w-full max-w-87.5 flex-col overflow-hidden rounded-2xl shadow-[0_30px_70px_-30px_#000]"
        style={{ background: nv.bg, border: `1px solid ${nv.border}` }}
      >
        <div
          className="flex shrink-0 items-center gap-2.5 px-4 py-3"
          style={{ borderBottom: `1px solid ${nv.border}` }}
        >
          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-(--color-primary) qv-mono text-[11px] font-bold text-(--color-text-on-primary)">
            QV
          </span>
          <span className="flex flex-col leading-tight">
            <b className="text-[14px] font-bold text-white">Lab Assistant</b>
            <small className="text-[11px]" style={{ color: nv.sub }}>
              AI-powered guidance
            </small>
          </span>
          <X className="ml-auto h-4 w-4" style={{ color: nv.sub }} />
        </div>
        <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto p-4">
          <div className="flex items-start gap-2.5">
            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-(--color-primary)/40 bg-(--color-primary-100) qv-mono text-[10px] font-bold text-(--color-primary)">
              QV
            </span>
            <div
              className="rounded-xl px-3.5 py-2 text-[12.5px] leading-relaxed"
              style={{
                background: nv.bubble,
                border: `1px solid ${nv.border}`,
                color: nv.text,
              }}
            >
              Hi! I'm your{" "}
              <b className="text-(--color-primary)">Lab Assistant</b> for this
              scenario.
              <br />
              <br />I can help guide your thinking without giving away the
              answers. What would you like to explore?
            </div>
          </div>
          <div className="mt-auto flex flex-col items-start gap-2">
            {[
              ["How do I start?", false],
              ["What tools should I use?", true],
              ["I'm stuck, give me a hint", false],
            ].map(([s, on]) => (
              <span
                key={s}
                className="rounded-full px-3.5 py-1.5 text-[12px]"
                style={
                  on
                    ? {
                        background: nv.chip,
                        border: "1px solid rgba(34,197,94,.55)",
                        color: "var(--color-primary)",
                      }
                    : {
                        background: nv.chip,
                        border: `1px solid ${nv.border}`,
                        color: "#c3cfe6",
                      }
                }
              >
                {s}
              </span>
            ))}
          </div>
        </div>
        <div
          className="flex shrink-0 gap-2.5 p-3"
          style={{ borderTop: `1px solid ${nv.border}` }}
        >
          <span
            className="flex-1 rounded-lg border px-3 py-2 text-[12px] text-(--color-text-muted)"
            style={{ borderColor: "rgba(34,197,94,.5)" }}
          >
            Ask about this lab…
          </span>
          <span
            className="flex w-12 items-center justify-center rounded-lg text-(--color-primary)"
            style={{
              background:
                "color-mix(in srgb, var(--color-primary) 34%, #0b140f)",
              border: "1px solid rgba(34,197,94,.5)",
            }}
          >
            <ArrowRight className="h-4 w-4" />
          </span>
        </div>
      </div>
    </div>
  );
}

/* ── Stage 2 · Omnirange-Agent (terminal) ─────────────────────────────────────── */
const BOOT = [
  { label: "spawning agent core", dots: 12, value: "online" },
  { label: "linking skill modules", dots: 10, value: "23 loaded" },
  { label: "opening session channel", dots: 8, value: "live" },
];

function OmnirangeStage() {
  const reduce = usePrefersReducedMotion();
  const [booted, setBooted] = useState(0);
  const [team, setTeam] = useState("RED");

  useEffect(() => {
    if (reduce) {
      setBooted(BOOT.length);
      return;
    }
    setBooted(0);
    setTeam("RED");
    const timers = BOOT.map((_, i) =>
      setTimeout(() => setBooted(i + 1), 420 * (i + 1)),
    );
    /* The stage is only on screen for ROTATE_MS before the section moves on,
       so the swap lands at the halfway mark — RED and BLUE each get an equal
       share of the time the panel is actually visible. */
    const cycle = setInterval(
      () => setTeam((t) => (t === "RED" ? "BLUE" : "RED")),
      ROTATE_MS / 2,
    );
    return () => {
      timers.forEach(clearTimeout);
      clearInterval(cycle);
    };
  }, [reduce]);

  const accent = team === "RED" ? "#e2564b" : "#5b9df0";

  return (
    <div
      className="absolute inset-0 flex flex-col overflow-hidden rounded-xl border border-[#1b2f24] p-3.5 qv-mono text-[10.5px] leading-relaxed"
      style={{ background: "#0a0f0d", color: "#7fdca4" }}
    >
      {/* shell header */}
      <div className="shrink-0 text-[#22c55e]">
        root@iZluy0oyaym2fZ:/# ctf-agent
      </div>

      <div className="flex min-h-0 flex-1 select-none items-center justify-center overflow-hidden py-1">
        <div
          aria-label="OMNIRANGE COPILOT"
          className="qv-glitch text-center"
          style={{
            fontSize: "clamp(20px, 3.6vw, 38px)",
            lineHeight: 1.3,
            letterSpacing: "0.03em",
            color: "#7dfcb0",
            textShadow: "0 0 16px rgba(34,197,94,.5)",
          }}
        >
          <div>OMNIRANGE</div>
          <div>COPILOT</div>
        </div>
      </div>

      {/* subtitle + rule */}
      <div className="flex shrink-0 items-center gap-3 text-[10px] text-[#5f8471]">
        <span>ctf solver buddy</span>
        <span className="text-[#22c55e]">▬▬▬▬</span>
        <span className="text-[#93b6a4]">v0.1</span>
      </div>
      <div className="mb-1.5 mt-1 h-px w-full bg-[rgba(34,197,94,.45)]" />

      {/* boot sequence — each line resolves in turn */}
      <div className="shrink-0 space-y-0 text-[9.5px]">
        {BOOT.map((b, i) => (
          <div key={b.label} className="flex items-center gap-1.5">
            <span className="text-[#22c55e]">▸</span>
            <span className="text-[#93b6a4]">{b.label}</span>
            <span className="text-[#5f8471]">{".".repeat(b.dots)}</span>
            <span
              className="text-[#22c55e]"
              style={{
                opacity: booted > i ? 1 : 0,
                transition: "opacity .3s ease",
              }}
            >
              ✓ {b.value}
            </span>
          </div>
        ))}
      </div>

      {/* mascot + greeting — kept compact so the wordmark keeps its room */}
      <div className="mt-1.5 flex shrink-0 items-center gap-1.5">
        <span
          aria-hidden="true"
          className="shrink-0 rounded border border-[#22c55e]/60 px-1 py-0.5 text-center leading-none text-[#22c55e]"
          style={{ boxShadow: "0 0 10px -4px rgba(34,197,94,.6)" }}
        >
          <span className="lp-copilot-blink block text-[9px]">•‿•</span>
        </span>
        <div className="rounded border border-[#22c55e]/60 px-2 py-1">
          <div className="text-[9.5px] text-[#e8f5ee]">
            Hey! I'm OMNI — your CTF solver buddy.
          </div>
          <div className="text-[8.5px] text-[#5f8471]">
            Ask me anything. Tab toggles RED ⇄ BLUE. Esc quits.
          </div>
        </div>
      </div>

      {/* prompt line — bracket syntax, team colour swaps with the mode */}
      <div className="mt-1.5 shrink-0 border-l border-[#22c55e]/60 pl-2">
        <div className="text-[10.5px]">
          <span className="text-[#5f8471]">(</span>
          <span className="text-[#93b6a4]">omni</span>
          <span
            className="mx-0.5 px-1 font-bold"
            style={{ background: accent, color: "#000" }}
          >
            ⣿
          </span>
          <span style={{ color: accent, fontWeight: 700 }}>{team}</span>
          <span className="text-[#5f8471]">)—[</span>
          <span className="text-[#22c55e]">ctf</span>
          <span className="text-[#5f8471]">]—[</span>
          <span className="text-[#93b6a4]">
            deepseek-v4-flash-free · 23 skills
          </span>
          <span className="text-[#5f8471]">]</span>
        </div>
        <div className="mt-0.5 flex items-center gap-1.5 text-[10.5px]">
          <span className="text-[#22c55e]">❯</span>
          <span
            className={`inline-block h-3 w-1.5 bg-[#22c55e] align-middle ${
              reduce ? "" : "animate-pulse"
            }`}
          />
          <span className="text-[#5f8471]">ask OMNI anything…</span>
        </div>
      </div>

      {/* status bar */}
      <div className="mt-1.5 flex shrink-0 flex-wrap items-center gap-x-2.5 gap-y-1 text-[9.5px]">
        <span
          className="px-1.5 py-0.5 font-bold"
          style={{ background: accent, color: "#000" }}
        >
          ⣿⣿ {team} TEAM
        </span>
        <span className="text-[#93b6a4]">Tab cycle</span>
        <span className="text-[#5f8471]">·</span>
        <span className="text-[#93b6a4]">Enter run</span>
        <span className="text-[#5f8471]">·</span>
        <span className="text-[#93b6a4]">Esc exit</span>
        <span className="ml-auto flex items-center gap-1.5 text-[#22c55e]">
          <span className="cyber-badge-dot" />
          session live
        </span>
      </div>
    </div>
  );
}

/* ── Stage 3 · AI Architect (chat + canvas topology) ────────────────────── */
const TOOLS = [Menu, ZoomIn, ZoomOut, Scan, Workflow, Maximize2];
const TOPO_BOXES = [
  { x: 118, y: 23, w: 84, label: "Load Balancer", cx: 160, cy: 34, live: true },
  { x: 47, y: 101, w: 76, label: "Web · 01", cx: 85, cy: 112 },
  { x: 197, y: 101, w: 76, label: "Web · 02", cx: 235, cy: 112 },
  { x: 72, y: 185, w: 76, label: "Database", cx: 110, cy: 196 },
  { x: 184, y: 185, w: 72, label: "Cache", cx: 218, cy: 196, live: true },
];
function ArchitectStage() {
  return (
    <div className="absolute inset-0 grid grid-cols-1 grid-rows-[minmax(0,1fr)_150px] overflow-hidden rounded-xl border border-(--color-border) sm:grid-cols-[210px_1fr] sm:grid-rows-1">
      {/* chat */}
      <div className="flex min-h-0 flex-col gap-2.5 border-b border-(--color-border) bg-(--color-bg-card) p-3 sm:border-b-0 sm:border-r">
        <div className="flex shrink-0 items-center gap-2">
          <Sparkles className="h-4 w-4 text-(--color-primary)" />
          <span className="text-[12px] font-bold tracking-wide text-(--color-text-primary)">
            AI ARCHITECT
          </span>
        </div>
        <div className="flex shrink-0 overflow-hidden rounded-lg border border-(--color-border) text-[10.5px]">
          <span className="flex-1 py-1.5 text-center text-(--color-text-muted)">
            Ask
          </span>
          <span className="flex-1 bg-(--color-primary-100) py-1.5 text-center font-semibold text-(--color-primary)">
            Agent
          </span>
          <span className="flex-1 py-1.5 text-center text-(--color-text-muted)">
            Plan
          </span>
        </div>
        <div className="flex min-h-0 flex-1 flex-col gap-2.5 overflow-y-auto">
          <div
            className="shrink-0 rounded-lg border border-(--color-border) p-2.5 text-[11px] leading-relaxed text-(--color-text-primary)"
            style={{
              background:
                "radial-gradient(120% 80% at 0 0, color-mix(in srgb, var(--color-primary) 12%, transparent), transparent 55%), var(--color-bg-card)",
            }}
          >
            Hello! I'm your AI topology assistant. Describe what you want to
            build and I'll generate it for you.
          </div>
          <div className="shrink-0 qv-mono text-[8.5px] tracking-widest text-(--color-text-muted)">
            TRY ASKING
          </div>
          {[
            "Create a basic web server topology with load balancer",
            "Design a secure network with firewall and VPN",
            "Add monitoring and logging infrastructure",
          ].map((s) => (
            <div
              key={s}
              className="shrink-0 rounded-lg border border-white/5 p-2 text-[10.5px] leading-snug text-(--color-text-muted)"
            >
              {s}
            </div>
          ))}
        </div>
        <div className="flex shrink-0 items-center gap-2 rounded-lg border border-(--color-border) py-1.5 pl-3 pr-1.5 text-[10.5px] text-(--color-text-muted)">
          Describe your topology…
          <span className="ml-auto flex h-6 w-6 items-center justify-center rounded-md bg-(--color-primary) text-(--color-text-on-primary)">
            <ArrowRight className="h-3.5 w-3.5" />
          </span>
        </div>
      </div>

      {/* canvas */}
      <div className="lp-arch-canvas relative">
        <Search className="absolute right-3 top-3 h-4 w-4 text-(--color-primary)" />
        <div className="absolute left-3 top-1/2 hidden -translate-y-1/2 flex-col gap-4 rounded-xl border border-(--color-border-solid) bg-(--color-bg-card) p-2.5 sm:flex">
          {TOOLS.map((T, i) => (
            <T
              key={i}
              className={`h-4 w-4 ${i === 0 ? "text-(--color-primary)" : "text-(--color-text-muted)"}`}
            />
          ))}
        </div>
        <svg
          viewBox="0 0 320 240"
          preserveAspectRatio="xMidYMid meet"
          className="absolute inset-0 h-full w-full"
        >
          {[
            "M160,34 L85,112",
            "M160,34 L235,112",
            "M85,112 L235,112",
            "M85,112 L110,196",
            "M235,112 L218,196",
          ].map((d) => (
            <path
              key={d}
              d={d}
              fill="none"
              stroke="var(--color-primary)"
              strokeOpacity="0.4"
              strokeWidth="1.2"
            />
          ))}
          {["M160,34 L85,112", "M160,34 L235,112", "M235,112 L218,196"].map(
            (d, i) => (
              <circle key={d} r="2.4" className="lp-topo-pulse">
                <animateMotion
                  dur={`${2.5 + i * 0.3}s`}
                  repeatCount="indefinite"
                  path={d}
                />
              </circle>
            ),
          )}
          {TOPO_BOXES.map((b) => (
            <g key={b.label}>
              <rect
                x={b.x}
                y={b.y}
                width={b.w}
                height="22"
                rx="6"
                className="lp-arch-node"
                stroke="var(--color-primary)"
                strokeOpacity="0.75"
                strokeWidth="1"
              />
              {b.live && (
                <circle
                  cx={b.x + b.w - 8}
                  cy={b.y + 4}
                  r="2.4"
                  fill="var(--color-primary)"
                  style={{
                    filter: "drop-shadow(0 0 4px var(--color-primary))",
                  }}
                />
              )}
              <text
                x={b.cx}
                y={b.y + 11}
                textAnchor="middle"
                dominantBaseline="central"
                fontSize="8.5"
                fontWeight="600"
                fontFamily="Inter, system-ui, sans-serif"
                fill="var(--color-text-primary)"
              >
                {b.label}
              </text>
            </g>
          ))}
        </svg>
        {/* minimap */}
        <div className="absolute bottom-3 right-3 h-14 w-22 rounded-lg border border-(--color-border-solid) bg-(--color-bg-card)" />
      </div>
    </div>
  );
}

const STAGES = [
  AdvisorStage,
  LabAssistantStage,
  OmnirangeStage,
  ArchitectStage,
];

export default function AIAssistants() {
  const [active, setActive] = useState(0);
  const reduce = usePrefersReducedMotion();
  const timer = useRef();

  const schedule = useCallback(() => {
    clearTimeout(timer.current);
    if (reduce) return;
    timer.current = setTimeout(
      () => setActive((a) => (a + 1) % COPILOTS.length),
      ROTATE_MS,
    );
  }, [reduce]);

  useEffect(() => {
    schedule();
    return () => clearTimeout(timer.current);
  }, [active, schedule]);

  const Stage = STAGES[active];

  return (
    <section id="ai" className="relative py-16 lg:py-24">
      <div className="mx-auto max-w-350 px-5 md:px-10">
        {/* heading */}
        {/* The wrapper runs wider than the headline so the code line below has
           room to breathe; the headline keeps its own narrower measure. */}
        <div className="lp-reveal mx-auto max-w-5xl text-center">
          <div className="mb-4 flex items-center justify-center gap-2 qv-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-(--color-primary)">
            <span className="h-px w-6 bg-(--color-primary)" />
            AI Copilots
            <span className="h-px w-6 bg-(--color-primary)" />
          </div>
          <h1
            className="lp-rise qv-glitch mt-6 text-[32px] sm:text-[40px] leading-tight font-normal tracking-tight text-(--color-text-primary)"
            style={{ "--lp-delay": "90ms" }}
          >
            {" "}
            Multiple AI assistants, embedded{" "}
            <span className="lp-highlight">where you work</span>
          </h1>
          <p
            className="qv-mono mt-6 border-l-2 pl-4 text-left text-[13px] leading-relaxed"
            style={{
              borderColor:
                "color-mix(in srgb, var(--color-primary) 40%, transparent)",
            }}
          >
            <span className="font-medium" style={{ color: "#c084fc" }}>
              const
            </span>{" "}
            <span style={{ color: "#2dd4bf" }}>AI_Assistants</span>{" "}
            <span className="text-(--color-text-muted)">=</span>{" "}
            <span
              style={{
                color:
                  "color-mix(in srgb, var(--color-primary) 38%, var(--color-text-secondary))",
              }}
            >
              "From the lab page to inside the attack box to the topology canvas
              — purpose-built copilots guide learners, sharpen operators, and
              design networks, each without ever handing over the answer."
            </span>
            <span className="text-(--color-text-muted)">;</span>
          </p>
        </div>

        {/* switcher */}
        <div className="lp-reveal mt-14 grid grid-cols-1 gap-6 lg:grid-cols-[0.8fr_1fr] lg:gap-8">
          {/* selector */}
          <div className="flex flex-col gap-3">
            {COPILOTS.map((c, i) => {
              const Icon = c.icon;
              const on = i === active;
              return (
                <button
                  key={c.name}
                  onClick={() => setActive(i)}
                  className={`group relative flex items-center gap-3 overflow-hidden rounded-2xl border p-4 text-left transition-all duration-200 ${on ? "border-(--color-primary)/40 bg-(--color-primary-50) shadow-[0_0_34px_-18px_var(--color-shadow-primary)]" : "border-(--color-border) hover:border-(--color-primary)/25"}`}
                >
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-(--color-border-solid) bg-(--color-primary-50) text-(--color-primary)">
                    <Icon className="h-5 w-5" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <b className="block text-[15px] font-semibold text-(--color-text-primary)">
                      {c.name}
                    </b>
                    <small className="text-[12px] text-(--color-primary)">
                      {c.role}
                    </small>
                  </span>
                  <span className="ml-auto hidden shrink-0 qv-mono text-[8.5px] tracking-wider text-(--color-text-muted) sm:block">
                    {c.tag}
                  </span>
                  {on && (
                    <span
                      key={active}
                      className="absolute inset-x-0 bottom-0 h-0.5 bg-(--color-bg)"
                    >
                      <span className="lp-copilot-fill block h-full bg-[linear-gradient(90deg,var(--color-primary),var(--color-primary-hover))]" />
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* stage frame */}
          <div className="flex flex-col overflow-hidden rounded-2xl border border-(--color-border) bg-(--color-bg-card) shadow-[0_40px_90px_-40px_#000]">
            <div className="flex items-center gap-2 border-b border-(--color-border) px-4 py-3">
              <span className="flex gap-1.5">
                <span className="h-2.5 w-2.5 rounded-full bg-[#ff5f57]/80" />
                <span className="h-2.5 w-2.5 rounded-full bg-[#febc2e]/80" />
                <span className="h-2.5 w-2.5 rounded-full bg-[#28c840]/80" />
              </span>
              <span className="qv-mono text-[11.5px] text-(--color-text-muted)">
                {COPILOTS[active].title}
              </span>
              <span className="ml-auto flex items-center gap-1.5 qv-mono text-[10px] text-(--color-primary)">
                <span className="cyber-badge-dot" />
                ACTIVE
              </span>
            </div>
            <div
              key={active}
              className="lp-copilot-fade relative min-h-110 flex-1 lg:min-h-0"
            >
              <Stage />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
